import type { AudioManager } from '../../audio/AudioManager';
import type { PointerEvent2, TouchInput } from '../../input/TouchInput';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '../../render/Renderer';
import {
  drawSilhouette,
  type SilhouetteId,
  worldHandAnchor,
} from '../../render/SilhouetteRenderer';
import { drawBlade } from '../../render/WeaponRenderer';
import { drawCombatBackground } from '../../render/CombatBackground';
import type { Enemy, Hero, Weapon, WeaponDefinition } from '../../types';
import type { Phase } from '../Phase';

const BAR_W = LOGICAL_WIDTH - 60;
const BAR_X = 30;
const BAR_Y = 320;
const BAR_H = 26;

type Stage = 'reveal' | 'live' | 'resolve';

export interface KillWindowOutcome {
  hit: boolean;
  quality: 'perfect' | 'good' | 'miss';
}

export class KillWindowSubphase implements Phase {
  private enemy: Enemy;
  private weapon: Weapon;
  private audio: AudioManager;
  private heroSilhouette: SilhouetteId;
  private enemySilhouette: SilhouetteId;

  private stage: Stage = 'reveal';
  private stageT = 0;
  private stageDur = 0;
  private done = false;
  private unbindDown: (() => void) | null = null;

  /** Scrub position 0..1 across bar; oscillates back and forth. */
  private scrubPhase = 0;
  private scrubDir = 1;
  /** Window center 0..1, randomized once on start. */
  private zoneCenter: number;
  private zoneWidth: number;
  private scrubSpeed: number; // full traversals per second (Hz/2 actually)

  private outcome: KillWindowOutcome | null = null;
  private resolveT = 0;

  constructor(
    _hero: Hero,
    enemy: Enemy,
    weapon: Weapon,
    weaponDef: WeaponDefinition,
    heroSilhouette: SilhouetteId,
    enemySilhouette: SilhouetteId,
    input: TouchInput,
    audio: AudioManager,
  ) {
    this.enemy = enemy;
    this.weapon = weapon;
    this.audio = audio;
    this.heroSilhouette = heroSilhouette;
    this.enemySilhouette = enemySilhouette;
    this.zoneWidth = weaponDef.killWindowWidth;
    this.scrubSpeed = weaponDef.killWindowSpeed;
    // Randomize zone center but keep it fully within the bar.
    const half = this.zoneWidth / 2;
    this.zoneCenter = half + Math.random() * (1 - this.zoneWidth);
    this.enterStage('reveal');
    this.unbindDown = input.onPointerDown((e) => this.handleDown(e));
  }

  private enterStage(s: Stage): void {
    this.stage = s;
    this.stageT = 0;
    switch (s) {
      case 'reveal':
        this.stageDur = 0.65;
        break;
      case 'live':
        this.stageDur = 8; // generous safety cap; player should tap well before
        break;
      case 'resolve':
        this.stageDur = 0.7;
        break;
    }
  }

  private handleDown(_e: PointerEvent2): void {
    if (this.stage !== 'live') return;
    // Grade the tap by how close the scrub is to the zone center.
    const distFromCenter = Math.abs(this.scrubPhase - this.zoneCenter);
    const halfZone = this.zoneWidth / 2;
    const perfectZone = halfZone * 0.4;
    let quality: 'perfect' | 'good' | 'miss';
    if (distFromCenter <= perfectZone) quality = 'perfect';
    else if (distFromCenter <= halfZone) quality = 'good';
    else quality = 'miss';
    this.outcome = { hit: quality !== 'miss', quality };
    if (quality === 'perfect') this.audio.play('strike_perfect');
    else if (quality === 'good') this.audio.play('strike_good');
    else this.audio.play('strike_miss');
    this.enterStage('resolve');
  }

  update(dt: number): void {
    if (this.done) return;
    this.stageT += dt;

    if (this.stage === 'live') {
      this.scrubPhase += this.scrubDir * this.scrubSpeed * dt;
      if (this.scrubPhase >= 1) {
        this.scrubPhase = 1;
        this.scrubDir = -1;
      } else if (this.scrubPhase <= 0) {
        this.scrubPhase = 0;
        this.scrubDir = 1;
      }
    } else if (this.stage === 'resolve') {
      this.resolveT += dt;
    }

    if (this.stageT >= this.stageDur) {
      switch (this.stage) {
        case 'reveal':
          this.enterStage('live');
          break;
        case 'live':
          // Safety: treat as miss if player never taps.
          this.outcome = { hit: false, quality: 'miss' };
          this.enterStage('resolve');
          break;
        case 'resolve':
          this.done = true;
          break;
      }
    }
  }

  isComplete(): boolean {
    return this.done;
  }

  getOutcome(): KillWindowOutcome | null {
    return this.outcome;
  }

  dispose(): void {
    this.unbindDown?.();
    this.unbindDown = null;
  }

  render(ctx: CanvasRenderingContext2D): void {
    drawCombatBackground(ctx, this.enemy.backgroundId);
    this.drawCharacters(ctx);
    this.drawBanner(ctx);
    this.drawBar(ctx);
    this.drawResolve(ctx);
  }

  private drawCharacters(ctx: CanvasRenderingContext2D): void {
    const heroX = LOGICAL_WIDTH * 0.28;
    const enemyX = LOGICAL_WIDTH * 0.72;
    const feetY = 470;
    const sway = this.stageT * 1.2;
    drawSilhouette(ctx, this.heroSilhouette, {
      cx: heroX,
      feetY,
      swayPhase: sway,
      flip: false,
    });
    const anchor = worldHandAnchor(this.heroSilhouette, heroX, feetY, false);
    drawBlade(ctx, this.weapon.shape, this.weapon.heat, {
      cx: anchor.x + 50,
      cy: anchor.y - 8,
      length: 92,
      rotation: anchor.rotation - 0.6,
      damage: this.weapon.damage,
    });
    // Enemy stumbles: tilted, recoiled away.
    drawSilhouette(ctx, this.enemySilhouette, {
      cx: enemyX,
      feetY,
      swayPhase: sway,
      flip: true,
      tilt: 0.22,
      recoil: -16,
      stumble: true,
    });
  }

  private drawBanner(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#ffd84a';
    ctx.font = 'bold 22px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    if (this.stage === 'reveal') {
      const alpha = Math.min(1, this.stageT / 0.3);
      ctx.fillStyle = `rgba(255, 216, 74, ${alpha})`;
      ctx.fillText('FINISH HIM', LOGICAL_WIDTH / 2, 220);
    } else {
      ctx.fillText('FINISH HIM', LOGICAL_WIDTH / 2, 220);
    }
    ctx.fillStyle = '#a8957a';
    ctx.font = '12px system-ui, sans-serif';
    ctx.fillText('Tap when the marker hits the zone', LOGICAL_WIDTH / 2, 252);
  }

  private drawBar(ctx: CanvasRenderingContext2D): void {
    if (this.stage === 'reveal') return;
    // Bar background.
    ctx.fillStyle = '#181412';
    ctx.fillRect(BAR_X, BAR_Y, BAR_W, BAR_H);
    // Zone highlight.
    const half = this.zoneWidth / 2;
    const zoneX = BAR_X + BAR_W * (this.zoneCenter - half);
    const zoneW = BAR_W * this.zoneWidth;
    ctx.fillStyle = 'rgba(127, 255, 170, 0.35)';
    ctx.fillRect(zoneX, BAR_Y, zoneW, BAR_H);
    // Inner perfect zone.
    const pHalf = half * 0.4;
    const pX = BAR_X + BAR_W * (this.zoneCenter - pHalf);
    const pW = BAR_W * (pHalf * 2);
    ctx.fillStyle = 'rgba(127, 255, 170, 0.6)';
    ctx.fillRect(pX, BAR_Y, pW, BAR_H);
    // Outline.
    ctx.strokeStyle = '#7a6a55';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(BAR_X, BAR_Y, BAR_W, BAR_H);
    // Scrub indicator.
    const sx = BAR_X + BAR_W * this.scrubPhase;
    ctx.fillStyle = '#ffe488';
    ctx.fillRect(sx - 2, BAR_Y - 6, 4, BAR_H + 12);
    ctx.fillStyle = '#3a2a16';
    ctx.fillRect(sx - 1, BAR_Y - 5, 2, BAR_H + 10);
  }

  private drawResolve(ctx: CanvasRenderingContext2D): void {
    if (this.stage !== 'resolve' || !this.outcome) return;
    const alpha = Math.max(0, 0.5 - this.resolveT * 1.0);
    const color =
      this.outcome.quality === 'perfect'
        ? `rgba(255, 240, 140, ${alpha})`
        : this.outcome.quality === 'good'
          ? `rgba(180, 240, 200, ${alpha})`
          : `rgba(220, 80, 60, ${alpha})`;
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT);
    ctx.fillStyle = '#f0d890';
    ctx.font = 'bold 28px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const label =
      this.outcome.quality === 'perfect'
        ? 'CLEAN KILL!'
        : this.outcome.quality === 'good'
          ? 'DECISIVE BLOW'
          : 'MISSED!';
    ctx.fillText(label, LOGICAL_WIDTH / 2, 130);
  }
}
